import { PDFDocument, rgb } from 'pdf-lib';

const hexToRgb = hex => {
  const normalized = (hex || '#FFEB3B').replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map(c => c + c)
          .join('')
      : normalized;
  const int = parseInt(full, 16);
  return {
    r: ((int >> 16) & 255) / 255,
    g: ((int >> 8) & 255) / 255,
    b: (int & 255) / 255
  };
};

/**
 * Bake highlight rectangles into a PDF.
 * Highlights use normalized page coords (0–1) with origin at top-left.
 */
export async function bakePdfHighlights(pdfSource, highlights = []) {
  if (!highlights.length) {
    if (typeof pdfSource === 'string') {
      const response = await fetch(pdfSource);
      return response.blob();
    }
    return pdfSource instanceof Blob ? pdfSource : new Blob([pdfSource], { type: 'application/pdf' });
  }

  let bytes;
  if (typeof pdfSource === 'string') {
    const response = await fetch(pdfSource);
    bytes = await response.arrayBuffer();
  } else if (pdfSource instanceof Blob) {
    bytes = await pdfSource.arrayBuffer();
  } else {
    bytes = pdfSource;
  }

  const pdfDoc = await PDFDocument.load(bytes);
  const pages = pdfDoc.getPages();

  highlights.forEach(highlight => {
    const page = pages[highlight.pageIndex];
    if (!page) return;

    const { width, height } = page.getSize();
    const color = hexToRgb(highlight.color);
    const x = (highlight.x || 0) * width;
    const w = (highlight.width || 0) * width;
    const h = (highlight.height || 0) * height;
    // Convert top-left normalized Y to PDF bottom-left origin
    const y = height - (highlight.y || 0) * height - h;

    page.drawRectangle({
      x,
      y,
      width: w,
      height: h,
      color: rgb(color.r, color.g, color.b),
      opacity: 0.4,
      borderWidth: 0
    });
  });

  const bakedBytes = await pdfDoc.save();
  return new Blob([bakedBytes], { type: 'application/pdf' });
}

/** Google-style highlight palette (10 focused colors). */
export const HIGHLIGHT_PALETTE = [
  { id: 'yellow', label: 'Yellow', value: '#FFEB3B' },
  { id: 'lime', label: 'Lime', value: '#CDDC39' },
  { id: 'green', label: 'Green', value: '#81C784' },
  { id: 'cyan', label: 'Cyan', value: '#4DD0E1' },
  { id: 'blue', label: 'Blue', value: '#64B5F6' },
  { id: 'purple', label: 'Purple', value: '#BA68C8' },
  { id: 'pink', label: 'Pink', value: '#F48FB1' },
  { id: 'orange', label: 'Orange', value: '#FFB74D' },
  { id: 'red', label: 'Red', value: '#E57373' },
  { id: 'gray', label: 'Gray', value: '#B0BEC5' }
];

export const HIGHLIGHT_COLORS = {
  yellow: HIGHLIGHT_PALETTE[0].value,
  pink: HIGHLIGHT_PALETTE[6].value
};

export const DEFAULT_HIGHLIGHT_COLOR = HIGHLIGHT_PALETTE[0].value;

export const createHighlightId = () =>
  `hl_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

/**
 * PDF.js text layers return many ClientRects per selection (one per span), often
 * overlapping around spaces/punctuation. Group by line and take the full bounding
 * box so one selected line becomes one highlight — no stacked "double" edges.
 * Rects are normalized page coords (0–1).
 */
export const mergeNormalizedHighlightRects = (rects = []) => {
  if (!Array.isArray(rects) || !rects.length) return [];

  const valid = rects.filter(r => r && r.width > 0 && r.height > 0);
  if (!valid.length) return [];

  const avgHeight = valid.reduce((sum, r) => sum + r.height, 0) / valid.length;
  const lineThreshold = Math.max(avgHeight * 0.65, 0.004);
  const sorted = [...valid].sort((a, b) => a.y - b.y || a.x - b.x);
  const lines = [];

  for (const rect of sorted) {
    const midY = rect.y + rect.height / 2;
    const line = lines.find(l => Math.abs(l.midY - midY) <= lineThreshold);

    if (!line) {
      lines.push({
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        midY
      });
      continue;
    }

    const right = Math.max(line.x + line.width, rect.x + rect.width);
    const bottom = Math.max(line.y + line.height, rect.y + rect.height);
    line.x = Math.min(line.x, rect.x);
    line.y = Math.min(line.y, rect.y);
    line.width = right - line.x;
    line.height = bottom - line.y;
    line.midY = line.y + line.height / 2;
  }

  return lines.map(({ x, y, width, height }) => ({ x, y, width, height }));
};

export const normalizePdfHighlights = (highlights = []) =>
  (Array.isArray(highlights) ? highlights : [])
    .filter(
      h =>
        h &&
        typeof h.pageIndex === 'number' &&
        typeof h.x === 'number' &&
        typeof h.y === 'number' &&
        typeof h.width === 'number' &&
        typeof h.height === 'number'
    )
    .map(h => ({
      id: h.id || createHighlightId(),
      pageIndex: h.pageIndex,
      type: h.type === 'text' ? 'text' : 'rect',
      color: h.color || DEFAULT_HIGHLIGHT_COLOR,
      x: h.x,
      y: h.y,
      width: h.width,
      height: h.height
    }));
